import { readFileSync } from 'fs';
import { join } from 'path';

import React, { FunctionComponent, useEffect, useState } from 'react';
import get from 'lodash/get';
import { Layout } from '@/components/Layout';
import Questionnaire from '@/components/Questionnaires/Questionnaire';
import { QuestionnairePageOptions } from '@/components/Questionnaires/QuestionnairePage';
import { convertToKebabCase, getShortHandle } from '@/components/Questionnaires/QuestionnaireUtils';
import VisaFinderAction from '@/components/Questionnaires/VisaFinder/VisaFinderAction';
import VisaFinderActionFooter from '@/components/Questionnaires/VisaFinder/VisaFinderActionFooter';
import { Col, Container, Hero, noop } from '@visalex/core';
import { Form, SelectOption } from '@visalex/forms';
import { useFormContext } from 'react-hook-form';

import { GetServerSideProps, NextPage } from 'next';
import { TFunction, Trans, useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

import commonStyles from '@/components/Questionnaires/Common/QuestionnairesCommon.module.scss';
import styles from '@/components/Questionnaires/VisaFinder/VisaFinder.module.scss';

export type FinderQuestion = {
  text: string;
  items: FinderQuestion[];
  action?: FinderAction;
};

export type FinderAction = {
  RuleAction: string;
  visaId?: string;
  url?: string;
  type?: string;
  leadToSchedule?: boolean;
  label: string;
  description: string;
};

const nameToField = (name: string) => ({ name, required: true });

export const VisaFinderRadio: FunctionComponent<{
  t: TFunction;
  handle: string;
  options: SelectOption[];
  answer?: number;
}> = ({ t, handle, options, answer }) => {
  const { setValue } = useFormContext();

  return (
    <Form.RadioGroup
      name={handle}
      rules={{ required: t('common.forms.common.rules.required') }}
      onChange={(newValue) => setValue(handle, newValue, { shouldValidate: true })}
      defaultValue={String(answer)}
      options={options}
    />
  );
};

/**
 * Creates the page with a single choice field (radio) given:
 * @param uuid — the unique ID of the question that prevents key duplication
 * @param question is the IFinderQuestion entry
 * @param answer numeric answer, the # of the selected radio button
 * @param t
 */
const questionToPage = (
  uuid: string,
  question: FinderQuestion,
  t: TFunction,
  answer?: number
): QuestionnairePageOptions => {
  const handle = getShortHandle(convertToKebabCase(question.text));
  const options: SelectOption[] = [];

  if (question.items)
    question.items.forEach((item, index) => {
      options.push({ label: item.text, value: String(index) });
    });

  return {
    uuid,
    title: question.text,
    fields: [handle].map(nameToField),
    children: <VisaFinderRadio t={t} options={options} handle={handle} answer={answer} />,
  };
};

const actionToPage = (uuid: string, action: FinderAction, t: TFunction): QuestionnairePageOptions => {
  return {
    uuid,
    title: 'Final Action',
    customFooter: <VisaFinderActionFooter {...action} />,
    fields: [],
    children: (
      <VisaFinderAction
        {...action}
        label={action.RuleAction === 'visa' ? `${t('visa-finder.eligible')} ${action.label}` : action.label}
      />
    ),
  };
};

const questionReducer = (question: FinderQuestion, answer: number) => {
  try {
    if (question.action) return question;

    if (get(question, `items.${answer}.action`, null)) return question.items[answer];

    return get(question, `items.${answer}.items.0`, null);
  } catch (e) {
    console.warn('Could not get the next question', { question, e });
  }

  return null;
};

const getUUID = (id: number, question: string, answers: number[] = []) => {
  return `ID: ${id}, Q: ${question}, Answers: ${JSON.stringify(answers)}`;
};

const getDefaultPage = (tree: FinderQuestion, t: TFunction) => questionToPage(getUUID(0, 'empty'), tree, t);

const createNewPage = (uuid: string, question: FinderQuestion, t: TFunction, answer?: number) => {
  return question.action ? actionToPage(uuid, question.action, t) : questionToPage(uuid, question, t, answer);
};

const recoverPages = (
  decisionTree: FinderQuestion,
  answers: { [questionHandle: string]: string },
  t: TFunction
): QuestionnairePageOptions[] => {
  if (decisionTree) {
    const pages: QuestionnairePageOptions[] = [];
    let currentNode: FinderQuestion | null = decisionTree;
    const questionHandles = Object.keys(answers);

    for (let i = 0; i < questionHandles.length; i++) {
      const currentHandle = questionHandles[i];
      const answer = parseInt(answers[currentHandle]);

      // Skip non-valid answers
      if (!/^[a-z-]+\d+$/.test(currentHandle) || !currentNode) continue;

      const checkHandle = `${getShortHandle(convertToKebabCase(currentNode.text))}`;

      if (currentHandle !== checkHandle || Number.isNaN(answer)) {
        continue;
      }

      pages.push(
        createNewPage(
          `${getUUID(pages.length, currentHandle, Object.values(answers).map(Number))}`,
          currentNode,
          t,
          answer
        )
      );
      currentNode = questionReducer(currentNode, Number(answers[checkHandle]));
    }

    if (currentNode) {
      const handle = `${getShortHandle(convertToKebabCase(currentNode.text))}`;

      pages.push(createNewPage(`${getUUID(pages.length, handle, Object.values(answers).map(Number))}`, currentNode, t));
    }

    return pages;
  }

  return [];
};

export type VisaFinderProps = { tree: FinderQuestion; query: Record<string, string> };

const VisaFinder: NextPage<VisaFinderProps> = ({ tree, query = {} }) => {
  const { t, i18n } = useTranslation();
  const [pages, setPages] = useState<QuestionnairePageOptions[]>(recoverPages(tree, query, t));
  const [updater, updateForm] = useState(0);

  useEffect(() => {
    if (Object.keys(query).length === 0) {
      window.scrollTo(0, 0);
    }

    if (i18n.language) {
      fetch(new URL(`/locales/${i18n.language || 'en'}/visa-finder.json`, location.origin))
        .then((res) => res.json())
        .then((newTree: FinderQuestion) => {
          setPages(recoverPages(newTree, query, t));
        })
        .catch(console.error);
    }
  }, [i18n.language]);

  return (
    <Layout title={t('visa-finder.title')} description={t('visa-finder.description')}>
      <div className={styles['visa-finder']}>
        <Hero className={commonStyles.questionnaire__hero}>
          <Col xs={12} xl={8} className="mx-auto">
            <h1>{t('visa-finder.title')}</h1>
            <p>
              <Trans i18nKey="visa-finder.subtitle">
                Use our <strong>Smart Visa Finder</strong> to discover the Green Card categories or visa types you
                should be considering. All you need to do is answer a few quick questions about yourself.
              </Trans>
            </p>
          </Col>
        </Hero>
        <Container className={commonStyles.questionnaire__container}>
          <Form key={updater} onSubmit={noop} mode="onChange" resetOnDefaultValuesChange>
            <Questionnaire
              loading={false}
              pages={pages}
              showDots={false}
              className={commonStyles.questionnaire__questionnaire}
              onPageCompleted={(_, answers) => {
                if (tree) setPages(recoverPages(tree, answers, t));
              }}
              onReset={() => {
                updateForm(updater + 1);

                if (tree) {
                  setPages([getDefaultPage(tree, t)]);
                }
              }}
            />
          </Form>
        </Container>
      </div>
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale, query }) => {
  const res = await readFileSync(join(process.cwd(), `./assets/locales/${locale || 'en'}/visa-finder.json`));
  const tree = JSON.parse(res.toString());

  if (!tree) {
    return {
      notFound: true,
    };
  }

  return {
    props: { tree, query, ...(await serverSideTranslations(locale as string)) }, // will be passed to the page component as props
  };
};

export default VisaFinder;
